import {
  Component,
  OnInit,
  ViewChild,
  Input,
  TemplateRef,
  Output,
  EventEmitter,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core'
import {
  CdkVirtualScrollViewport,
  ScrollDispatcher,
} from '@angular/cdk/scrolling'
import {
  Observable,
  BehaviorSubject,
  Subscription,
  Subject,
  interval,
} from 'rxjs'
import {takeUntil, throttle, filter, tap} from 'rxjs/operators'

export interface InfiniteListConfig {
  rowIdentifier?: string
  striped?: boolean
  minWidth?: string
  triggerThresholdPercentage?: number
}

export interface ScrollEndEvent {
  direction: loadDirection
}

export enum loadDirection {
  UP = 'up',
  DOWN = 'down',
  NONE = 'none',
}

@Component({
  selector: 'app-infinite-scroll-list',
  templateUrl: './infinite-scroll-list.component.html',
  styleUrls: ['./infinite-scroll-list.component.scss'],
  standalone: false,
})
export class InfiniteScrollListComponent implements OnInit, OnDestroy {
  @ViewChild(CdkVirtualScrollViewport, {static: true})
  viewport: CdkVirtualScrollViewport
  defaultConfig = {
    rowIdentifier: 'id',
    striped: true,
    minWidth: '1200px',
    triggerThresholdPercentage: 25,
  }

  @Input() config: InfiniteListConfig = this.defaultConfig
  @Input() data$: Observable<Array<Object>>
  @Input() listItemTemplateRef: TemplateRef<any>
  @Output() onScrollEnd = new EventEmitter<ScrollEndEvent>()
  @Output() onTop = new EventEmitter<boolean>()

  prevTop = 0
  prevBottom = 0
  scrollEventSubject$ = new BehaviorSubject(null)
  scrollSubscription: Subscription
  direction = loadDirection.NONE
  destroy$ = new Subject<boolean>()
  scrollTopPosition$ = new BehaviorSubject(0)

  constructor(
    private scrollDispatcher: ScrollDispatcher,
    public changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.scrollSubscription = this.scrollEventSubject$
      .pipe(
        filter((val) => val !== null),
        throttle(() => interval(1000)),
        tap((val) => {
          this.emitScrollEvent(val.direction)
        }),
        takeUntil(this.destroy$),
      )
      .subscribe()

    this.scrollDispatcher.scrolled(50).subscribe(() => {
      this.contentScrolled()
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next(true)
    this.destroy$.unsubscribe()
  }

  contentScrolled() {
    /**
     * prevTop and prevBottom is used to determine the scroll direction
     * triggerThreshold is where the scroll end event kicks in (default 25%)
     */
    const threshold = this.config.triggerThresholdPercentage
      ? this.config.triggerThresholdPercentage
      : 25
    const top = this.viewport.measureScrollOffset('top')
    const bottom = this.viewport.measureScrollOffset('bottom')
    if (top === 0) {
      this.prevTop = top
      this.prevBottom = bottom
      this.scrollEventSubject$.next({direction: loadDirection.UP, top: top})
      this.scrollTopPosition$.next(top)
      return
    }
    if (bottom === 0) {
      this.prevTop = top
      this.prevBottom = bottom
      this.scrollEventSubject$.next({direction: loadDirection.DOWN, top: top})
      this.scrollTopPosition$.next(top)
      return
    }
    const triggerThreshold = Math.floor((top + bottom) * (threshold / 100))
    if (Math.floor(top - this.prevTop) > triggerThreshold) {
      this.prevTop = top
      this.prevBottom = bottom
      this.scrollTopPosition$.next(top)
      return
    }
    if (
      top < triggerThreshold &&
      this.prevTop > triggerThreshold &&
      top < this.prevTop
    ) {
      this.direction = loadDirection.UP
      this.scrollEventSubject$.next({direction: loadDirection.UP, top: top})
    }
    if (bottom < triggerThreshold && bottom < this.prevBottom) {
      this.direction = loadDirection.DOWN
      this.scrollEventSubject$.next({direction: loadDirection.DOWN, top: top})
    }
    this.prevTop = top
    this.prevBottom = bottom
    this.scrollTopPosition$.next(top)
  }

  emitScrollEvent(loadDirection: loadDirection) {
    this.onScrollEnd.emit({direction: loadDirection})
  }

  detectChanges() {
    this.changeDetectorRef.detectChanges()
  }
}
